import { ReactElement } from "react";
import { DTO } from "src/common/utils/types";
import {
  initializeObtinedViaForCurrentAccessControl,
  ObtainedViaForCurrentAccessControl,
} from "./obtainedViaForAccessControl";
import { initializeScope, isGlobalScope, Scope } from "./scope";

type ResourceForCurrentAccessControl = {
  id: string;
  name: string;
  group: string;
  domain: string;
};
type SystemResourceForCurrentAccessControl = {
  systemId: string;
  systemResourceId: string;
  systemResourceType: string;
  attributes: Map<string, any>;
  name: string;
};

class SystemResourceAttribute {
  name: string;
  value: string | Array<string> | null;
  sequence: number;
  isHyperlink: boolean;

  constructor(input: DTO<SystemResourceAttribute>) {
    this.name = input.name;
    this.isHyperlink = input.isHyperlink;
    this.sequence = input.sequence;
    this.value = input.value;
  }

  render(): ReactElement {
    const renderUrl = (url: string) => (
      <a
        className="linkSpan"
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        {url}
      </a>
    );
    if (Array.isArray(this.value)) {
      return (
        <div className={`dynamic-attribute-${this.name}-cell`}>
          {this.value.map((val) => (
            <div key={val} className="array-value">
              {this.isHyperlink ? renderUrl(val) : val}
            </div>
          ))}
        </div>
      );
    } else if (this.value !== null) {
      return (
        <div className={`dynamic-attribute-${this.name}-cell`}>
          {this.isHyperlink ? renderUrl(this.value as string) : this.value}
        </div>
      );
    } else {
      return <div className={`dynamic-attribute-${this.name}-cell`}>--</div>;
    }
  }
}

export class CurrentAccessControlBasedOnSystemResource {
  static mandatoryAttributes = [
    "resource",
    "systemResource",
    "obtainedVia",
    "systemResourceAttributes",
    "scopes",
  ];
  resource: ResourceForCurrentAccessControl;
  systemResource: SystemResourceForCurrentAccessControl;
  obtainedVia: ObtainedViaForCurrentAccessControl;
  systemResourceAttributes: Array<SystemResourceAttribute>;
  scopes: Array<Scope>;
  rowSpan: { systemResourceSpan: number; obtainedViaSpan: number; resourceSpan: number };

  constructor(
    input: Object,
    geographies: { allCountries: Array<string>; allRegions: Array<string> }
  ) {
    for (const [key, value] of Object.entries(input)) {
      if (key !== "obtainedVia" && key !== "systemResourceAttributes" && key !== "scopes") {
        this[key] = value;
      }
    }
    const inputKeys = Array.from(Object.keys(input));
    const missedMandatoryFields =
      CurrentAccessControlBasedOnSystemResource.mandatoryAttributes.filter(
        (mandatoryAttribute) => !inputKeys.includes(mandatoryAttribute)
      );
    if (missedMandatoryFields.length > 0) {
      console.log(missedMandatoryFields.join(", "));
      throw new Error(
        `Following fields are not present on creating Current Access Control Based On System Resource object ${missedMandatoryFields.join(
          ", "
        )}`
      );
    }
    this.obtainedVia = initializeObtinedViaForCurrentAccessControl(
      input["obtainedVia"] as ObtainedViaForCurrentAccessControl
    );
    this.systemResourceAttributes = input["systemResourceAttributes"].map(
      (systemResourceAttribute: DTO<SystemResourceAttribute>) =>
        new SystemResourceAttribute(systemResourceAttribute)
    );
    this.scopes = input["scopes"].map((scope: Scope) => initializeScope(scope, geographies));
  }

  renderScope(): ReactElement {
    return <div className="scope-cell">{this.scopes[0].name}</div>;
  }

  renderScopeValue(): ReactElement {
    const firstScope = this.scopes[0];
    return (
      <div className="scope-value-cell">{firstScope.values?.render(isGlobalScope(firstScope))}</div>
    );
  }

  renderDynamicColumn(columnName: string): ReactElement {
    return (
      this.systemResourceAttributes
        .find((systemResourceAttribute) => systemResourceAttribute.name === columnName)
        ?.render() ?? <div className={`dynamic-attribute-cell`}>--</div>
    );
  }

  renderResource(): ReactElement {
    return (
      <div className="resource-cell">
        <div className="name">{this.resource.name}</div>
        <div>
          <b>Group:</b> {this.resource.group}
        </div>
        <div>
          <b>Domain:</b> {this.resource.domain}
        </div>
      </div>
    );
  }

  renderSystemResource(): ReactElement {
    return (
      <div className="system-resource-cell">
        <div className="name">{this.systemResource.name}</div>
        <div>
          <b>Id:</b> {this.systemResource.systemResourceId}
        </div>
      </div>
    );
  }

  systemResourceCombinedId(): string {
    return JSON.stringify({
      systemId: this.systemResource.systemId,
      systemResourceId: this.systemResource.systemResourceId,
    });
  }

  resourceId(): string {
    return this.resource.id;
  }

  obtainedViaCombinedId(): string {
    return this.obtainedVia.stringifiedId();
  }

  calculateIdentifiers(): { systemResource: string; resource: string; obtainedVia: string } {
    return {
      systemResource: this.systemResourceCombinedId(),
      resource: this.resourceId(),
      obtainedVia: this.obtainedViaCombinedId(),
    };
  }

  setRowSpan(rowSpan: {
    systemResourceSpan: number;
    obtainedViaSpan: number;
    resourceSpan: number;
  }) {
    this.rowSpan = rowSpan;
  }
}

export class CurrentAccessControlsBasedOnSystemResource {
  currentAccessControls: Array<CurrentAccessControlBasedOnSystemResource>;

  constructor(
    inputs: Array<DTO<CurrentAccessControlBasedOnSystemResource>>,
    isScopeApplicable: boolean,
    geographies: { allCountries: Array<string>; allRegions: Array<string> }
  ) {
    if (isScopeApplicable) {
      this.currentAccessControls = [];
      inputs.forEach((input) => {
        this.currentAccessControls = [
          ...this.currentAccessControls,
          // @ts-ignore
          ...input.scopes.map(
            (scope: Scope) =>
              new CurrentAccessControlBasedOnSystemResource({
                ...input,
                scopes: [scope],
              },geographies)
          ),
        ];
      });
    } else {
      this.currentAccessControls = inputs.map(
        (input) => new CurrentAccessControlBasedOnSystemResource(input, geographies)
      );
    }
    this.calculateRowSpans();
  }

  calculateRowSpans() {
    const systemResourceSpan = new Map<number, number>();
    const obtainedViaSpan = new Map<number, number>();
    const resourceSpan = new Map<number, number>();
    if (this.currentAccessControls.length === 0) {
      return;
    }
    let previousIdentifiers = this.currentAccessControls[0].calculateIdentifiers();
    let lastRepeatedResourceCount = 1;
    let lastRepeatedSystemResourceCount = 1;
    let lastRepeatedObtainedViaCount = 1;
    let index: number = 1;
    for (index; index < this.currentAccessControls.length; index++) {
      const currentIdentifiers = this.currentAccessControls[index].calculateIdentifiers();
      if (currentIdentifiers.obtainedVia === previousIdentifiers.obtainedVia) {
        lastRepeatedObtainedViaCount = lastRepeatedObtainedViaCount + 1;
      } else {
        obtainedViaSpan.set(index - lastRepeatedObtainedViaCount, lastRepeatedObtainedViaCount);
        lastRepeatedObtainedViaCount = 1;
      }
      if (currentIdentifiers.resource === previousIdentifiers.resource) {
        lastRepeatedResourceCount = lastRepeatedResourceCount + 1;
      } else {
        resourceSpan.set(index - lastRepeatedResourceCount, lastRepeatedResourceCount);
        lastRepeatedResourceCount = 1;
      }
      if (currentIdentifiers.systemResource === previousIdentifiers.systemResource) {
        lastRepeatedSystemResourceCount = lastRepeatedSystemResourceCount + 1;
      } else {
        systemResourceSpan.set(
          index - lastRepeatedSystemResourceCount,
          lastRepeatedSystemResourceCount
        );
        lastRepeatedSystemResourceCount = 1;
      }
      previousIdentifiers = this.currentAccessControls[index].calculateIdentifiers();
    }
    obtainedViaSpan.set(index - lastRepeatedObtainedViaCount, lastRepeatedObtainedViaCount);
    resourceSpan.set(index - lastRepeatedResourceCount, lastRepeatedResourceCount);
    systemResourceSpan.set(
      index - lastRepeatedSystemResourceCount,
      lastRepeatedSystemResourceCount
    );
    this.currentAccessControls.forEach((value, index) => {
      value.setRowSpan({
        systemResourceSpan: systemResourceSpan.get(index) ?? 0,
        obtainedViaSpan: obtainedViaSpan.get(index) ?? 0,
        resourceSpan: resourceSpan.get(index) ?? 0,
      });
    });
  }
}
